import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Modal from '../../../ui/layout/Modal';
import Loader from '../../../ui/primitives/Loader';
import TableComponent from '../../../ui/data/table/TableComponent';
import PaginationComponent from '../../../ui/data/table/PaginationComponent';
import DetailsPageLink from '../../../components/elements/DetailsPageLink';
import { getRepositoryRedundancyInsights } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const TestKeyLink = ({ testKey, projectId, variant = 'body2' }) => (
  <Typography variant={variant} component="span" fontWeight={600}>
    <DetailsPageLink
      to={`/project/${projectId}/testcases/${testKey}`}
      label={testKey}
      openInNewTab
    />
  </Typography>
);

const getTestListColumns = (projectId) => [
  {
    key: 'key',
    label: 'Key',
    minWidth: 100,
    formatContent: (row) => <TestKeyLink testKey={row.key} projectId={projectId} />,
  },
  { key: 'summary', label: 'Summary', minWidth: 260 },
  { key: 'execution_count', label: 'Executions', minWidth: 100 },
  {
    key: 'last_executed_at',
    label: 'Last Executed',
    minWidth: 130,
    formatContent: (row) => formatDate(row.last_executed_at),
  },
];

const RedundancyDatasetModal = ({ open, onClose, dataset, title, projectId }) => {
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const isPaginatedDataset = dataset === 'rarely-used' || dataset === 'frequent-manual';

  useEffect(() => {
    if (open) {
      setPage(1);
      setPerPage(10);
      setSelected([]);
    }
  }, [open, dataset]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEYS.project.redundancyDataset(projectId, dataset, page, perPage),
    queryFn: () => getRepositoryRedundancyInsights(projectId, dataset, page, perPage),
    enabled: open && !!projectId && !!dataset,
  });

  const rows = useMemo(() => {
    if (!data) return [];
    if (dataset === 'rarely-used') return data.rarely_used_tests || [];
    if (dataset === 'frequent-manual') return data.frequently_executed_manual || [];
    return [];
  }, [data, dataset]);

  const clusters = dataset === 'similar' ? data?.duplicate_clusters || [] : [];
  const totalCount = data?.pagination?.total_count ?? rows.length;
  const clustersTotalCount = data?.total_count ?? clusters.length;
  const testListColumns = useMemo(() => getTestListColumns(projectId), [projectId]);

  const handlePageChange = (newPage) => setPage(newPage);
  const handlePerPageChange = (e) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(1);
  };

  const disableRowHoverSx = {
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
   backgroundColor: 'transparent !important',  
  },
 }

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ py: 4 }}>
          <Loader minHeight={160}/>
        </Box>
      );
    }

    if (isError) {
      return (
        <Typography variant="body2" color="error.main">
          {error?.message || 'Failed to load data.'}
        </Typography>
      );
    }

    if (dataset === 'similar') {
      if (!clusters.length) {
        return (
          <Typography variant="body2" color="text.secondary">
            No duplicate clusters found.
          </Typography>
        );
      }

      return (
        <>
          <Stack spacing={2}>
            {clusters.map((cluster, index) => (
              <Box
                key={cluster.representative?.id ?? index}
                sx={{
                  p: 2,
                  borderRadius: (t) => `${t.shape.borderRadius}px`,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                  <Box sx={{ minWidth: 0 }}>
                    <TestKeyLink testKey={cluster.representative?.key} projectId={projectId} variant="caption" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      {cluster.representative?.summary}
                    </Typography>
                  </Box>
                {/* <Chip
                  label={`${cluster.similar_count} similar · avg ${cluster.average_match_percentage}`}
                  size="small"
                  color="warning"
                  sx={{ fontWeight: 700, flexShrink: 0 }}
                /> */}
                </Stack>

                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  {(cluster.similar_tests || []).map((test) => (
                    <Stack
                      key={test.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        p: 1,
                        pl: 1.5,
                        borderRadius: (t) => `${t.shape.borderRadius}px`,
                        bgcolor: (t) => alpha(t.palette.warning.main, 0.07),
                        borderLeft: (t) => `3px solid ${alpha(t.palette.warning.main, 0.5)}`,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <TestKeyLink testKey={test.key} projectId={projectId} variant="caption" />
                        <Typography variant="body2" noWrap>
                          {test.summary}
                        </Typography>
                      </Box>
                      <Chip 
                        label={test.match_percentage} 
                        size="small" 
                        sx= {(t) => ({
                          flexShrink: 0,
                          fontWeight: 700,
                          color: t.palette.warning.dark,
                          bgcolor: alpha(t.palette.warning.main, 0.16),
                          })}
                          />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>

          <PaginationComponent
            totalCount={clustersTotalCount}
            currentPage={page}
            perPage={perPage}
            handlePageChange={handlePageChange}
          />
        </>
      );
    }

    if (!rows.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No test cases found.
        </Typography>
      );
    }

    return (
      <Box sx={disableRowHoverSx}>
      <TableComponent
        data={rows}
        headerTabs={testListColumns}
        tablekey={`redundancy-${dataset}`}
        page={page}
        perPage={perPage}
        totalCount={totalCount}
        showCheckbox={false}
        showPages={isPaginatedDataset}
        handlePageChange={handlePageChange}
        selected={selected}
        setSelected={setSelected}
        order="asc"
        setOrder={() => {}}
        defaultSort="key"
        isLoading={false}
        showCount={isPaginatedDataset}
        showRowCount={isPaginatedDataset}
        setPerPage={handlePerPageChange}
      />
      </Box>
    );
  };

  return (
    <Modal open={open} handleClose={onClose} modalTitle={title || 'Details'} width="md">
      {renderBody()}
    </Modal>
  );
};

export default RedundancyDatasetModal;